import { Suspense, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { CommandPalette } from "@/components/CommandPalette";
import { KeyboardShortcutsHelp } from "@/components/KeyboardShortcutsHelp";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { WelcomeTourProvider } from "@/components/tour";
import { ContextualTourProvider } from "@/components/tour/ContextualTourProvider";
import Index from "@/pages/Index";
import React from "react";

// Lazy load non-critical components
const Ferramentas = React.lazy(() => import("@/pages/Ferramentas"));
const NotFound = React.lazy(() => import("@/pages/NotFound"));
const Auth = React.lazy(() => import("@/pages/Auth"));
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const Admin = React.lazy(() => import("@/pages/Admin"));
const Planos = React.lazy(() => import("@/pages/Planos"));
const ToolDemo = React.lazy(() => import("@/pages/ToolDemo"));
const Isca = React.lazy(() => import("@/pages/Isca"));
const KitsImplementacao = React.lazy(() => import("@/pages/KitsImplementacao"));
const ChecklistExecution = React.lazy(() => import("@/pages/ChecklistExecution"));
const SobreNos = React.lazy(() => import("@/pages/SobreNos"));
const Contato = React.lazy(() => import("@/pages/Contato"));
const TermosUso = React.lazy(() => import("@/pages/TermosUso"));
const PoliticaPrivacidade = React.lazy(() => import("@/pages/PoliticaPrivacidade"));
const CentralDados = React.lazy(() => import("@/pages/CentralDados"));

// Lazy load all planilhas components
const FundamentosMarketing = React.lazy(() => import("@/pages/planilhas/FundamentosMarketing"));
const DefinicaoProduto = React.lazy(() => import("@/pages/planilhas/DefinicaoProduto"));
const GestaoMarketing = React.lazy(() => import("@/pages/planilhas/GestaoMarketing"));
const PrecificacaoEstrategica = React.lazy(() => import("@/pages/planilhas/PrecificacaoEstrategica"));
const PlanejamentoTrafego = React.lazy(() => import("@/pages/planilhas/PlanejamentoTrafego"));
const CronogramaFluxos = React.lazy(() => import("@/pages/planilhas/CronogramaFluxos"));
const RetencaoCliente = React.lazy(() => import("@/pages/planilhas/RetencaoCliente"));
const MarketingConteudo = React.lazy(() => import("@/pages/planilhas/MarketingConteudo"));
const ProjecaoAnual = React.lazy(() => import("@/pages/planilhas/ProjecaoAnual"));
const SocialMediaPostManager = React.lazy(() => import("@/pages/planilhas/SocialMediaPostManager"));
const InitialSalesTracker = React.lazy(() => import("@/pages/planilhas/InitialSalesTracker"));
const LaunchPlanning = React.lazy(() => import("@/pages/planilhas/LaunchPlanning"));
const ABTestingCampaigns = React.lazy(() => import("@/pages/planilhas/ABTestingCampaigns"));
const MultiChannelContentManager = React.lazy(() => import("@/pages/planilhas/MultiChannelContentManager"));
const DigitalCashFlow = React.lazy(() => import("@/pages/planilhas/DigitalCashFlow"));
const MarketingAutomationPro = React.lazy(() => import("@/pages/planilhas/MarketingAutomationPro"));
const CompetitorAnalysisPro = React.lazy(() => import("@/pages/planilhas/CompetitorAnalysisPro"));
const ProductLadderPro = React.lazy(() => import("@/pages/planilhas/ProductLadderPro"));
const TeamContractorManagement = React.lazy(() => import("@/pages/planilhas/TeamContractorManagement"));

// Planilhas Consolidadas (NOVAS)
const AffiliateManagementUnified = React.lazy(() => import("@/pages/planilhas/AffiliateManagementUnified"));
const LeadManagementUnified = React.lazy(() => import("@/pages/planilhas/LeadManagementUnified"));
const TrafficCentralUnified = React.lazy(() => import("@/pages/planilhas/TrafficCentralUnified"));
const MetaAdsPlanning = React.lazy(() => import("@/pages/planilhas/MetaAdsPlanning"));
const FirstFunnelBuilder = React.lazy(() => import("@/pages/planilhas/FirstFunnelBuilder"));
const FunnelManagementPro = React.lazy(() => import("@/pages/planilhas/FunnelManagementPro"));
const CustomerJourneyComplete = React.lazy(() => import("@/pages/planilhas/CustomerJourneyComplete"));
const FinancialPlanning = React.lazy(() => import("@/pages/planilhas/FinancialPlanning"));
const ContentHubUnified = React.lazy(() => import("@/pages/planilhas/ContentHubUnified"));
const FinanceHubUnified = React.lazy(() => import("@/pages/planilhas/FinanceHubUnified"));
const FunnelHubUnified = React.lazy(() => import("@/pages/planilhas/FunnelHubUnified"));
const MarketingHubUnified = React.lazy(() => import("@/pages/planilhas/MarketingHubUnified"));
const PropostaComercial = React.lazy(() => import("@/pages/planilhas/PropostaComercial"));
const SOPTemplates = React.lazy(() => import("@/pages/planilhas/SOPTemplates"));
const ProductHubUnified = React.lazy(() => import("@/pages/planilhas/ProductHubUnified"));
const QAChecklist = React.lazy(() => import("@/pages/planilhas/QAChecklist"));
const OnboardingConfig = React.lazy(() => import("@/pages/planilhas/OnboardingConfig"));
const Guias = React.lazy(() => import("@/pages/Guias"));
const DesafiosImplementacao = React.lazy(() => import("@/pages/DesafiosImplementacao"));
const ChallengeDetails = React.lazy(() => import("@/pages/ChallengeDetails"));
const AIPromptExample = React.lazy(() => import("@/pages/AIPromptExample"));
const SharedConversation = React.lazy(() => import("@/pages/SharedConversation"));
const Conversations = React.lazy(() => import("@/pages/Conversations"));
const Chat = React.lazy(() => import("@/pages/Chat"));
const SmartCheck = React.lazy(() => import("@/pages/SmartCheck"));
const SmartCheckExecution = React.lazy(() => import("@/pages/SmartCheckExecution"));

export function AppContent() {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);

  // Initialize keyboard shortcuts (now safely inside Router context)
  useKeyboardShortcuts({
    shortcuts: [
      {
        key: "?",
        shift: true,
        description: "Mostrar Atalhos",
        action: () => setShortcutsHelpOpen(true),
        category: "Geral",
      },
    ],
  });

  return (
    <WelcomeTourProvider>
      <ContextualTourProvider>
      <CommandPalette 
        open={commandPaletteOpen} 
        onOpenChange={setCommandPaletteOpen}
        onShowHelp={() => setShortcutsHelpOpen(true)}
      />
      <KeyboardShortcutsHelp 
        open={shortcutsHelpOpen}
        onOpenChange={setShortcutsHelpOpen}
      />
      <Suspense
        fallback={
          <div 
            className="flex flex-col items-center justify-center min-h-screen gap-4"
            role="status"
            aria-live="polite"
            aria-label="Carregando página"
          >
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" aria-hidden="true"></div>
            <p className="text-sm text-muted-foreground">Carregando...</p>
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/ferramentas" element={<Ferramentas />} />
          {/* Demo removido - redirect para trial */}
          <Route path="/demo" element={<Auth />} />
          <Route path="/planilhas/fundamentos-marketing" element={<FundamentosMarketing />} />
          <Route path="/planilhas/gestao-marketing" element={<GestaoMarketing />} />
          <Route path="/planilhas/planejamento-trafego" element={<PlanejamentoTrafego />} />
          <Route path="/planilhas/retencao-cliente" element={<RetencaoCliente />} />
          <Route path="/planilhas/social-media-post-manager" element={<SocialMediaPostManager />} />
          <Route path="/planilhas/initial-sales-tracker" element={<InitialSalesTracker />} />
          <Route path="/planilhas/launch-planning" element={<LaunchPlanning />} />
          <Route path="/planilhas/ab-testing-campaigns" element={<ABTestingCampaigns />} />
          <Route path="/planilhas/multi-channel-content-manager" element={<MultiChannelContentManager />} />
          <Route path="/planilhas/digital-cash-flow" element={<DigitalCashFlow />} />
          <Route path="/planilhas/marketing-automation-pro" element={<MarketingAutomationPro />} />
          <Route path="/planilhas/competitor-analysis-pro" element={<CompetitorAnalysisPro />} />
          <Route path="/planilhas/team-contractor-management" element={<TeamContractorManagement />} />
          
          {/* Planilhas Consolidadas - Novas */}
          <Route path="/planilhas/affiliate-management-unified" element={<AffiliateManagementUnified />} />
          <Route path="/planilhas/lead-management-unified" element={<LeadManagementUnified />} />
          <Route path="/planilhas/traffic-central-unified" element={<TrafficCentralUnified />} />
          <Route path="/planilhas/meta-ads-planning" element={<MetaAdsPlanning />} />
          <Route path="/planilhas/first-funnel-builder" element={<FirstFunnelBuilder />} />
          <Route path="/planilhas/funnel-management-pro" element={<FunnelManagementPro />} />
          <Route path="/planilhas/customer-journey-complete" element={<CustomerJourneyComplete />} />
          <Route path="/planilhas/financial-planning" element={<FinancialPlanning />} />
          <Route path="/planilhas/content-hub" element={<ContentHubUnified />} />
          <Route path="/planilhas/finance-hub" element={<FinanceHubUnified />} />
          <Route path="/planilhas/funnel-hub" element={<FunnelHubUnified />} />
          <Route path="/planilhas/marketing-hub" element={<MarketingHubUnified />} />
          <Route path="/planilhas/proposta-comercial" element={<PropostaComercial />} />
          <Route path="/planilhas/sop-templates" element={<SOPTemplates />} />
          <Route path="/planilhas/product-hub" element={<ProductHubUnified />} />
          <Route path="/planilhas/qa-checklist" element={<QAChecklist />} />
          <Route path="/planilhas/onboarding-config" element={<OnboardingConfig />} />
          
          {/* 
            Redirects movidos para src/config/redirects.ts
            Para implementação no servidor (Vercel/Netlify), ver:
            - generateVercelRedirects() para vercel.json
            - generateNetlifyRedirects() para _redirects
            
            Mantidos aqui temporariamente para compatibilidade client-side
          */}
          <Route path="/planilhas/planejamento-meta-ads" element={<MetaAdsPlanning />} />
          <Route path="/planilhas/planejamento-meta-ads-avancado" element={<MetaAdsPlanning />} />
          <Route path="/planilhas/simple-lead-tracker" element={<LeadManagementUnified />} />
          <Route path="/planilhas/advanced-lead-management" element={<LeadManagementUnified />} />
          <Route path="/planilhas/advanced-lead-qualification" element={<LeadManagementUnified />} />
          <Route path="/planilhas/basic-paid-traffic-planner" element={<TrafficCentralUnified />} />
          <Route path="/planilhas/advanced-traffic-metrics" element={<TrafficCentralUnified />} />
          <Route path="/planilhas/basic-affiliate-tracker" element={<AffiliateManagementUnified />} />
          <Route path="/planilhas/affiliate-management-pro" element={<AffiliateManagementUnified />} />
          <Route path="/planilhas/affiliate-management-premium" element={<AffiliateManagementUnified />} />
          <Route path="/planilhas/first-funnel-checklist" element={<FirstFunnelBuilder />} />
          <Route path="/planilhas/planejamento-funis" element={<FirstFunnelBuilder />} />
          <Route path="/planilhas/intermediate-funnel-management" element={<FunnelManagementPro />} />
          <Route path="/planilhas/advanced-funnel-management" element={<FunnelManagementPro />} />
          <Route path="/planilhas/simple-customer-journey-map" element={<CustomerJourneyComplete />} />
          <Route path="/planilhas/advanced-customer-journey-map" element={<CustomerJourneyComplete />} />
          <Route path="/planilhas/initial-financial-goals" element={<FinancialPlanning />} />
          <Route path="/planilhas/metas-financeiras" element={<FinancialPlanning />} />
          
          {/* Phase 3: Product Hub Consolidation Redirects */}
          <Route path="/planilhas/definicao-produto" element={<ProductHubUnified />} />
          <Route path="/planilhas/product-ladder-pro" element={<ProductHubUnified />} />
          <Route path="/planilhas/precificacao-estrategica" element={<ProductHubUnified />} />
          
          {/* Content Hub Consolidation Redirects */}
          <Route path="/planilhas/marketing-conteudo" element={<ContentHubUnified />} />
          <Route path="/planilhas/cronograma-fluxos" element={<ContentHubUnified />} />
          
          {/* Finance Hub Consolidation Redirects */}
          <Route path="/planilhas/projecao-anual" element={<FinanceHubUnified />} />
          
          <Route path="/guias" element={<Guias />} />
          <Route path="/desafios" element={<DesafiosImplementacao />} />
          <Route path="/desafios/:id" element={<ChallengeDetails />} />
          <Route path="/kits-implementacao" element={<KitsImplementacao />} />
          <Route path="/checklist-execution" element={<ChecklistExecution />} />
          <Route path="/sobre-nos" element={<SobreNos />} />
          <Route path="/contato" element={<Contato />} />
          <Route path="/termos-uso" element={<TermosUso />} />
          <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/central-dados" element={<CentralDados />} />
          <Route path="/planos" element={<Planos />} />
          <Route path="/demo/:toolId" element={<ToolDemo />} />
          <Route path="/isca" element={<Isca />} />
          <Route path="/assistentes-ia" element={<AIPromptExample />} />
          <Route path="/share/:shareToken" element={<SharedConversation />} />
          <Route path="/conversas" element={<Conversations />} />
          <Route path="/chat/:type/:toolId" element={<Chat />} />
          <Route path="/smartcheck" element={<SmartCheck />} />
          <Route path="/smartcheck/:id" element={<SmartCheckExecution />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </ContextualTourProvider>
    </WelcomeTourProvider>
  );
}
